import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const RedisOSSInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const redisEndpoint = annotations['redis-oss.infrastructure/redis-endpoint'];
  const redisVersion = annotations['redis-oss.infrastructure/redis-version'];
  const redisCliCommand = annotations['redis-oss.infrastructure/redis-cli-command'];
  const redisConfigInfo = annotations['redis-oss.infrastructure/redis-config-info'];
  const testDataInfo = annotations['redis-oss.infrastructure/test-data-info'];
  const dataVolumeId = annotations['redis-oss.infrastructure/data-volume-id'];
  const dataVolumeSize = annotations['redis-oss.infrastructure/data-volume-size'];

  const awsInstanceId = annotations['aws.infrastructure/instance-id'];
  const awsPublicIp = annotations['aws.infrastructure/public-ip'];
  const awsInstanceType = annotations['aws.infrastructure/instance-type'];
  const awsRegion = annotations['aws.infrastructure/region'];
  const awsConsoleUrl = annotations['aws.infrastructure/console-url'];
  const awsSsmCommand = annotations['redis-oss.infrastructure/ssm-command'];

  const gcpInstanceId = annotations['gcp.infrastructure/instance-id'];
  const gcpPublicIp = annotations['gcp.infrastructure/public-ip'];
  const gcpMachineType = annotations['gcp.infrastructure/machine-type'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const gcpConsoleUrl = annotations['gcp.infrastructure/console-url'];
  const gcpGcloudSshCommand = annotations['redis-oss.infrastructure/gcloud-ssh-command'];

  const privateIp = annotations['redis-oss.infrastructure/private-ip'];
  const sshPrivateKeyEncoded = annotations['redis-oss.infrastructure/ssh-private-key'];
  const sshCommand = annotations['redis-oss.infrastructure/ssh-command'];
  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];

  if (!templateType?.includes('redis-oss')) {
    return null;
  }

  const isAWS = templateType?.startsWith('aws-');
  const isGCP = templateType?.startsWith('gcp-');

  const instanceId = awsInstanceId || gcpInstanceId;
  const publicIp = awsPublicIp || gcpPublicIp;
  const instanceType = awsInstanceType || gcpMachineType;
  const region = awsRegion || gcpZone;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'redis_oss_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <InfoCard
      title="🔴 Redis OSS Instance"
      subheader={`Environment: ${environmentName || 'N/A'} | Version: ${redisVersion || 'N/A'}`}
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="🔌 Redis Connection">
          {redisEndpoint && <CopyableValue label="Redis Endpoint:" value={redisEndpoint} />}
          {redisCliCommand && <CopyableValue label="Redis CLI Command:" value={redisCliCommand} />}
          {redisVersion && (
            <InfraField
              label="Redis Version:"
              value={<InfraTagValue>{redisVersion}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        {(redisConfigInfo || testDataInfo) && (
          <InfraSection title="⚙️ Redis Configuration">
            {redisConfigInfo && <InfraField label="Configuration:" value={redisConfigInfo} />}
            {testDataInfo && <InfraField label="Test Data:" value={testDataInfo} />}
          </InfraSection>
        )}

        <InfraSection title={`🖥️ ${isAWS ? 'EC2' : 'Compute'} Instance`}>
          {instanceId && (
            <InfraField
              label="Instance ID:"
              value={
                <span className={styles.copyRow}>
                  {isAWS && (
                    <ExternalLink
                      href={
                        awsConsoleUrl ||
                        `https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion || 'us-east-1'}#InstanceDetails:instanceId=${instanceId}`
                      }
                    >
                      {instanceId}
                    </ExternalLink>
                  )}
                  {isGCP && gcpConsoleUrl && (
                    <ExternalLink href={gcpConsoleUrl}>{instanceId}</ExternalLink>
                  )}
                  {isGCP && !gcpConsoleUrl && instanceId}
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label={isAWS ? 'Instance Type:' : 'Machine Type:'}
              value={instanceType}
            />
          )}
          {region && (
            <InfraField label={isAWS ? 'Region:' : 'Zone:'} value={region} />
          )}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
          {privateIp && <CopyableValue label="Private IP:" value={privateIp} />}
        </InfraSection>

        {(dataVolumeId || dataVolumeSize) && (
          <InfraSection title="💾 Storage">
            {dataVolumeId && <CopyableValue label="Data Volume ID:" value={dataVolumeId} />}
            {dataVolumeSize && (
              <InfraField label="Data Volume Size:" value={`${dataVolumeSize} GB`} />
            )}
          </InfraSection>
        )}

        {(sshPrivateKey || sshCommand || awsSsmCommand || gcpGcloudSshCommand) && (
          <InfraSection title="🔧 SSH Access">
            {sshPrivateKey && (
              <>
                <InfraField label="SSH Private Key:" value="" mono={false} />
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
                <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                  💡 After downloading, run <code>chmod 400 redis_oss_key.pem</code>
                </Text>
              </>
            )}
            {sshCommand && <CopyableValue label="SSH Command:" value={sshCommand} />}
            {awsSsmCommand && <CopyableValue label="SSM Command:" value={awsSsmCommand} />}
            {gcpGcloudSshCommand && (
              <CopyableValue label="gcloud SSH Command:" value={gcpGcloudSshCommand} />
            )}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
